import { AppThunk } from '../../../store/reducers';
import { POST, PUT } from '../../../service/HttpMethod';
import { toastFunction } from '../../../helpers/toastShow';
import { InitialState, ActionConstant, ActionType, Details } from './types';
import {
	APP_TOKEN,
	REFRESH_TOKEN,
	USER_ID,
	USER_ROLE,
	SUBSCRIPTION_STATUS,
	TERMS_CONDITIONS,
	TRADE_CREATION,
	RESTRICT_PROJECT_CREATION,
} from '../../../helpers/Constants';
import { syncTermsAcceptanceFromServer } from '../../../legal/legalDocuments';
import {
	confirmBidderPasswordReset,
	loginBidderAccount,
	requestBidderPasswordReset,
} from '../../../features/bidInvites/publicApi';

const initialState: InitialState = {
	statusCode: 0,
	userdetails: {
		id: 0,
		role: '',
		token: '',
		refreshToken: '',
		subscriptionStatus: '',
		tradeCreation: '',
		restrictProjectCreationToAdmins: '',
	},
};
const saveTolocalStorage = (data: any = {}) => {
	const keyarr: any = [];
	const valarr: any = [];
	for (const key of Object.keys(data)) {
		keyarr.push(key);
	}
	for (const val of Object.values(data)) {
		valarr.push(val);
	}
	for (let i = 0; i < keyarr.length; i += 1) {
		// save to local storage
		localStorage.setItem(keyarr[i], valarr[i]);
	}
};
const SaveUserDetailsAction = (data: Details): ActionType => ({
	type: ActionConstant.GET_USER_DETAILS,
	data,
});
export const clearUserDetails = (): ActionType => ({ type: ActionConstant.CLEAR_USER_DETAILS });
export function logIn(datas: any = {}): AppThunk {
	const postConfig = {
		data: datas,
		url: `login`,
	};
	return async (dispatch) => {
		try {
			const { status = 0, data = {} } = await POST(postConfig);
			const {
				token,
				refreshToken = 'NO REFRESH TOKEN YET',
				id,
				role,
				subscriptionStatus,
				termsAndConditions,
				tradeCreation,
				restrictProjectCreationToAdmins,
			} = data;
			saveTolocalStorage({
				[APP_TOKEN]: token,
				[REFRESH_TOKEN]: refreshToken,
				[USER_ID]: id,
				[USER_ROLE]: role,
				[SUBSCRIPTION_STATUS]: subscriptionStatus,
				[TERMS_CONDITIONS]: termsAndConditions,
				[TRADE_CREATION]: tradeCreation,
				[RESTRICT_PROJECT_CREATION]: restrictProjectCreationToAdmins, // Restrict project creation to admins only
			});
			syncTermsAcceptanceFromServer(termsAndConditions);
			localStorage.setItem('NOTIFICATION_STATUS', 'true');
			dispatch(clearUserDetails());
			dispatch(SaveUserDetailsAction(data));

			dispatch({ type: ActionConstant.GET_STATUS, status });

			if (role === 'OA' || role === 'A' || role === 'U') {
				// role based redirection
				window.location.href = '/home';
			}
			if (role === 'PO') {
				window.location.href = '/dashboard';
			}
			if (role === 'BIDDER') {
				window.location.href = '/bidder/profile';
			}
		} catch (error) {
			try {
				const bidder = await loginBidderAccount(datas.email, datas.password);
				localStorage.setItem(USER_ID, String(bidder.account.id));
				dispatch(clearUserDetails());
				dispatch({ type: ActionConstant.GET_STATUS, status: 200 });
				window.location.href = '/bidder/profile';
			} catch (bidderError) {
				const message =
					bidderError?.response?.data?.message ||
					error?.data?.message ||
					'Invalid email or password';
				dispatch(toastFunction('Error', message, ''));
				dispatch({ type: ActionConstant.GET_STATUS, status: 400 });
			}
		}
	};
}

export function forgotBidderPassword(email: string): AppThunk {
	return async (dispatch) => {
		try {
			await requestBidderPasswordReset(email);
			dispatch({ type: ActionConstant.GET_STATUS, status: 200 });
			window.location.href = `/forgotpasswordsuccess/?account=bidder&email=${encodeURIComponent(
				email,
			)}`;
		} catch (error) {
			dispatch({ type: ActionConstant.GET_STATUS, status: 0 });
			dispatch(
				toastFunction(
					'Error',
					error?.response?.data?.message || 'Unable to request a bidder password reset',
					'',
				),
			);
		}
	};
}

export function resetBidderPassword(token: string, password: string): AppThunk {
	return async (dispatch) => {
		try {
			await confirmBidderPasswordReset(token, password);
			dispatch({ type: ActionConstant.GET_STATUS, status: 200 });
			if (localStorage.getItem(USER_ROLE) === 'BIDDER') {
				localStorage.removeItem(APP_TOKEN);
				localStorage.removeItem(USER_ID);
				localStorage.removeItem(USER_ROLE);
				for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
					const key = sessionStorage.key(index);
					if (key && key.indexOf('BIDDER_SESSION_') === 0) sessionStorage.removeItem(key);
				}
			}
			localStorage.setItem('reset', 'true');
			window.location.href = '/';
		} catch (error) {
			dispatch({ type: ActionConstant.GET_STATUS, status: 0 });
			dispatch(
				toastFunction(
					'Error',
					error?.response?.data?.message || 'Unable to reset the bidder password',
					'',
				),
			);
		}
	};
}

export function forgotPassword(datas: any = {}): AppThunk {
	// forgot password
	const putConfig = {
		data: datas,
		url: `forgotPassword`,
	};
	return async (dispatch) => {
		try {
			const { status = 0 } = await PUT(putConfig);
			dispatch({ type: ActionConstant.GET_STATUS, status });

			window.location.href = `/forgotpasswordsuccess/?email=${encodeURIComponent(
				String(datas.email || ''),
			)}`;
		} catch (error) {
			dispatch({ type: ActionConstant.GET_STATUS, status: 0 });
			dispatch(toastFunction('Error', error.data.message, ''));
		}
	};
}

export function resetPassword(datas: any = {}): AppThunk {
	// password reset
	const data: any = {};
	data.email = datas.email;
	data.password = datas.password;
	data.code = datas.code;
	const putConfig = {
		data,
		url: `resetPassword`,
	};
	return async (dispatch) => {
		try {
			const { status = 0 } = await PUT(putConfig);
			dispatch({ type: ActionConstant.GET_STATUS, status });
			localStorage.setItem('reset', 'true');
			window.location.href = '/';
		} catch (error) {
			dispatch({ type: ActionConstant.GET_STATUS, status: 0 });
			dispatch(toastFunction('Error', error.data.message, ''));
		}
	};
}

export function ssoLogin(datas: any = {}): AppThunk {
	const postConfig = {
		data: datas,
		url: `/ssoLogin`,
	};
	return async (dispatch) => {
		try {
			const { status = 0, data = {} } = await POST(postConfig);
			const {
				token,
				refreshToken = 'NO REFRESH TOKEN YET',
				id,
				role,
				subscriptionStatus,
				termsAndConditions,
				tradeCreation,
				restrictProjectCreationToAdmins,
			} = data;
			saveTolocalStorage({
				[APP_TOKEN]: token,
				[REFRESH_TOKEN]: refreshToken,
				[USER_ID]: id,
				[USER_ROLE]: role,
				[SUBSCRIPTION_STATUS]: subscriptionStatus,
				[TERMS_CONDITIONS]: termsAndConditions,
				[TRADE_CREATION]: tradeCreation,
				[RESTRICT_PROJECT_CREATION]: restrictProjectCreationToAdmins, // Restrict project creation to admins only
			});
			syncTermsAcceptanceFromServer(termsAndConditions);
			localStorage.setItem('NOTIFICATION_STATUS', 'true');
			dispatch(clearUserDetails());
			dispatch(SaveUserDetailsAction(data));

			dispatch({ type: ActionConstant.GET_STATUS, status });

			if (role === 'OA' || role === 'A' || role === 'U') {
				// role based redirection
				window.location.href = '/home';
			}
			if (role === 'PO') {
				window.location.href = '/dashboard';
			}
			if (role === 'BIDDER') {
				window.location.href = '/bidder/profile';
			}
		} catch (error) {
			if (error && error.data && error.data.message) {
				dispatch(toastFunction('Error', error.data.message, ''));
				window.location.href = '/';
			} else {
				dispatch(toastFunction('Error', 'Something went wrong', ''));
			}
			dispatch({ type: ActionConstant.GET_STATUS, status: 400 });
		}
	};
}

function loginReducer(state = initialState, action: ActionType): InitialState {
	switch (action.type) {
		case ActionConstant.GET_USER_DETAILS: {
			const { data } = action;
			return {
				...state,
				userdetails: data,
			};
		}
		case ActionConstant.GET_STATUS: {
			return {
				...state,
				statusCode: action.status,
			};
		}
		case ActionConstant.CLEAR_USER_DETAILS: {
			return {
				...state,
				userdetails: {
					id: 0,
					role: '',
					token: '',
					refreshToken: '',
					subscriptionStatus: '',
					tradeCreation: '',
					restrictProjectCreationToAdmins: '',
				},
			};
		}
		case ActionConstant.CLEAR_STATUS: {
			return {
				...state,
				statusCode: 0,
			};
		}
		default:
			return state;
	}
}

export default loginReducer;
